import * as Sentry from "@sentry/nextjs"
import { Spinner, TextInput } from "flowbite-react"
import { ErrorMessage, Form, Formik, FormikHelpers, FormikProps } from "formik"
import React, { ReactNode, useMemo, useState } from "react"
import useSWR from "swr"
import * as Yup from "yup"

import useAuth from "@hooks/useAuth"
import useToast from "@hooks/useToast"
import GiftCardSymbolIcon from "@icons/GiftCardSymbol"
import { AddGiftCardProductFormAttributes } from "@modules/tenants/OrdersPage/Components/AddOrderModal/AddGiftCardProductForm/AddGiftcardProductForm"
import ParticipantsFields from "@modules/tenants/OrdersPage/Components/AddOrderModal/ParticipationDetailsForm/ParticipantsFields"
import * as OrderServices from "@mono/essentials/services/OrderServices"

export type AddParticipantsFormAttributes = {
  participantsDetails?: Record<string, boolean | string | string[] | number>[]
}
interface AddGiftCardInfoProps {
  onSuccess: (arg0: any) => void
  selectedProduct: string
  isIntegratedPage: boolean
  units: number
  participantDetailsRef?: React.RefObject<FormikProps<any>>
  productValidityData: any
  formInitialValues?: AddParticipantsFormAttributes
}

const CurrencySymbol = () => {
  const { publicDomainDetails } = useAuth()
  return <div className={"text-neutral-400"}>{publicDomainDetails?.currency_symbol}</div>
}

const CustomAmountComponent = ({ productValidityData, onchange }) => {
  const [showCustomAmount, setShowCustomAmount] = useState(false)
  const [selectedAmount, setSelectedAmount] = useState<number | string>("")

  const handleSelectAmount = (value?: string) => {
    setSelectedAmount(value)
    onchange(parseInt(value.replace(/,/g, ""), 10))
  }

  return (
    <>
      <div
        className={
          "bg-primary-100 border-b border-t border-primary-300 p-3 my-2 gap-2 flex flex-wrap items-center"
        }
      >
        {productValidityData?.amount_options?.map((item, key) => (
          <div
            key={key}
            className={`${selectedAmount === item?.display_amount ? "border-primary-500" : "border-transparent"} border  bg-white py-2 px-1 sm:px-3 text14Medium rounded-md cursor-pointer`}
            onClick={() => {
              handleSelectAmount(item?.display_amount)
              setShowCustomAmount(false)
            }}
            data-testid={"gift-card-variant"}
          >
            {item?.amount_formatted}
          </div>
        ))}
        {productValidityData?.is_custom_amount_allowed && (
          <div
            className={`${showCustomAmount ? "border-primary-500" : "border-transparent"} border bg-white py-2 px-1 sm:px-3 text14Medium rounded-md cursor-pointer`}
            onClick={() => {
              handleSelectAmount("")
              setShowCustomAmount(true)
            }}
          >
            Custom
          </div>
        )}
      </div>
      {showCustomAmount && (
        <div
          className={
            "bg-primary-100 border-b border-t border-primary-300 p-3 my-2 gap-2 flex items-center"
          }
        >
          <TextInput
            className={"bg-white"}
            type={"number"}
            placeholder={"Enter Amount"}
            onChange={(e) => handleSelectAmount(e.target.value)}
            icon={CurrencySymbol}
          />
        </div>
      )}
    </>
  )
}

const AddGiftCardInfo = ({
  onSuccess,
  selectedProduct,
  isIntegratedPage,
  units,
  participantDetailsRef,
  productValidityData,
  formInitialValues,
}: AddGiftCardInfoProps) => {
  const { showToast } = useToast()

  const { data: productFieldsData, isLoading } = useSWR(
    selectedProduct ? `GiftproductFieldsData-${selectedProduct}` : null,
    async () => {
      try {
        const selectProductId = selectedProduct || ""
        const { data: getProductFieldsResponse } = await OrderServices.getProductFields(
          selectProductId,
          isIntegratedPage
        )
        if (getProductFieldsResponse) {
          return getProductFieldsResponse.data
        }
      } catch (e) {
        Sentry.captureException(e)
        console.error(e.response.data.message)
        showToast({ heading: e.response.data.message, type: "danger" })
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
      shouldRetryOnError: false,
    }
  )

  const initialValues = useMemo<any | undefined>(() => {
    if (productFieldsData) {
      const participantsFieldsArray: Record<string, boolean | string | string[] | number>[] = []
      Array(parseInt(`${units ?? 0}`, 10))
        .fill(1)
        .forEach((_, index) => {
          const fields: Record<string, boolean | string | string[] | number> = {}
          productFieldsData.forEach((productField) => {
            if (productField.allowed_online_booking && productField.ask?.includes("ask_per_item")) {
              fields[productField.field.name] = productField.field.type === "checkbox" ? [] : ""
              fields[`${productField.field.name}_id`] = productField.id ?? ""
            }
            fields.amount =
              productValidityData?.amount_options?.length === 1
                ? parseInt(productValidityData?.amount_options[0]?.display_amount.replace(/,/g, ""))
                : ""
          }),
            10
          participantsFieldsArray.push(fields)
        })
      return {
        participantsDetails: formInitialValues?.participantsDetails ?? participantsFieldsArray,
      }
    }
  }, [productFieldsData, units, productValidityData])

  const validationSchema = useMemo(() => {
    if (productFieldsData) {
      const fields: Record<string, any> = {}
      productFieldsData.forEach((productField) => {
        if (productField.allowed_online_booking && productField.ask?.includes("ask_per_item")) {
          fields[productField.field.name] = productField.required_for_online_booking
            ? Yup.string().required(`*${productField.label} Required`)
            : Yup.string()
          fields.amount = Yup.string().required(`*Amount is Required`)
        }
      })

      return Yup.object().shape({
        participantsDetails: Yup.array().required().of(Yup.object().shape(fields)),
      })
    }
  }, [productFieldsData, units])

  const onSubmit = async (
    values: AddGiftCardProductFormAttributes,
    { setSubmitting, setFieldError, resetForm }: FormikHelpers<AddGiftCardProductFormAttributes>
  ) => {
    try {
      setSubmitting(true)
      onSuccess(values)
    } catch (e: any) {
      Sentry.captureException(e)
      console.error(e?.response?.data?.message)
      showToast({ heading: e?.response?.data?.message, type: "danger" })
      const errors = e?.response?.data?.errors || []
      for (const key of Object.keys(errors)) {
        setFieldError(key, errors[key][0])
      }
    } finally {
      setSubmitting(false)
      resetForm()
    }
  }

  if (isLoading) {
    return (
      <div className={"text-center mt-5"}>
        <Spinner aria-label={"Center-aligned"} />
      </div>
    )
  }

  return (
    <Formik
      initialValues={initialValues}
      innerRef={participantDetailsRef}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ setFieldValue, values, errors }) => (
        <Form autoComplete={"off"}>
          <div
            className={`grid grid-cols-1 md:grid-cols-${units > 1 ? 2 : 1} gap-6 rounded-md mt-6 `}
          >
            {values?.participantsDetails?.map((participantDetails, _index) => (
              <div className={"w-full mb-2 bg-white relative shadow rounded-lg"} key={_index}>
                <GiftCardSymbolIcon className={"absolute z-auto"} />
                <div className={"py-6 sm:px-6 px-4"} data-testid={"gift-card-variant"}>
                  <div className={"relative mt-4"}>
                    {productValidityData?.amount_options?.length === 1 ? (
                      <div
                        className={"bg-primary-100 border-b border-t border-primary-300 p-3 my-2"}
                      >
                        <div className={"text14Regular text-neutral-700"}>
                          Amount:{" "}
                          <span
                            className={"text-neutral-1000 text14Semibold"}
                          >{`${productValidityData?.amount_options[0]?.amount_formatted}`}</span>
                        </div>
                      </div>
                    ) : (
                      <CustomAmountComponent
                        productValidityData={productValidityData}
                        onchange={(amountValue) => {
                          const updatedParticipantsDetails = [...values.participantsDetails]
                          updatedParticipantsDetails[_index].amount = amountValue

                          setFieldValue("participantsDetails", updatedParticipantsDetails)
                        }}
                      />
                    )}
                  </div>
                  {Object.keys(errors).length > 0 &&
                    errors?.participantsDetails[_index]?.amount && (
                      <p className={`text12Regular text-red-600`}>
                        {errors?.participantsDetails[_index]?.amount as ReactNode}
                      </p>
                    )}
                  <div className={"flex flex-col gap-1.5"}>
                    <ParticipantsFields
                      fieldsData={
                        productFieldsData?.map((field) => {
                          // TODO: The 'Delivery Date' field should be of type "delivery_date" from the backend.
                          if (field.label === "Delivery Date") {
                            return {
                              ...field,
                              field: {
                                ...field.field,
                                type: "delivery_date",
                              },
                            }
                          }
                          return field
                        }) ?? []
                      }
                      fieldGroupFormName={"participantsDetails"}
                      fieldArrayIndex={_index}
                      type={"ask_per_item"}
                      isWidgetPage
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default AddGiftCardInfo
